import { useCallback, useEffect, useRef } from "react";
import { css, cx } from "styled-system/css";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { cf } from "@/components/brand/sfturnos-styles";
import { useGoogleMapsLoader } from "@/hooks/use-google-maps-loader";

export type LocationValue = {
  address: string;
  lat: number | null;
  lng: number | null;
  placeId: string | null;
};

type Props = {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
};

const DEFAULT_CENTER = { lat: -34.6037, lng: -58.3816 }; // Obelisco, Buenos Aires
const MAP_CONTAINER_STYLE = { width: "100%", height: "280px" };

// google.maps.places.PlaceAutocompleteElement (Places API New) es un Web Component:
// se monta imperativamente porque no tiene wrapper JSX oficial. El @types/google.maps
// instalado todavía documenta la generación anterior del componente (marcada "beta" ahí),
// mientras que el runtime que sirve Google ya expone el evento `gmp-select` estable con
// `placePrediction` — por eso el listener abajo tipa el evento como PlaceAutocompleteEvent
// en lugar de confiar en los tipos del paquete.
type PlaceAutocompleteEvent = Event & {
  placePrediction: { toPlace: () => google.maps.places.Place };
};

// @types/google.maps todavía no declara PlaceAutocompleteElement dentro de PlacesLibrary
// (queda documentado aparte, marcado "beta"), aunque el runtime real de Google sí lo expone.
type PlacesLibraryWithAutocomplete = google.maps.PlacesLibrary & {
  PlaceAutocompleteElement: typeof google.maps.places.PlaceAutocompleteElement;
};

export function SettingsLocationBlock({ value, onChange }: Props) {
  const { isLoaded, loadError, apiKeyConfigured } = useGoogleMapsLoader();
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;

  useEffect(() => {
    if (!isLoaded || !autocompleteContainerRef.current || elementRef.current) return;
    let cancelled = false;
    void (async () => {
      const { PlaceAutocompleteElement } = (await google.maps.importLibrary(
        "places",
      )) as PlacesLibraryWithAutocomplete;
      if (cancelled || !autocompleteContainerRef.current) return;
      const el = new PlaceAutocompleteElement({});
      el.style.width = "100%";
      (el as HTMLElement).setAttribute("placeholder", "Buscar dirección…");
      autocompleteContainerRef.current.appendChild(el);
      elementRef.current = el as HTMLElement;

      el.addEventListener("gmp-select", (evt: Event) => {
        void (async () => {
          const { placePrediction } = evt as PlaceAutocompleteEvent;
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ["formattedAddress", "location", "id"] });
          onChangeRef.current({
            address: place.formattedAddress ?? valueRef.current.address,
            lat: place.location?.lat() ?? null,
            lng: place.location?.lng() ?? null,
            placeId: place.id ?? null,
          });
        })();
      });
    })();
    return () => {
      cancelled = true;
      elementRef.current?.remove();
      elementRef.current = null;
    };
  }, [isLoaded]);

  const hasPin = value.lat != null && value.lng != null;
  const center = hasPin ? { lat: value.lat as number, lng: value.lng as number } : DEFAULT_CENTER;

  const placePinAt = useCallback((lat: number, lng: number) => {
    onChangeRef.current({ ...valueRef.current, lat, lng });
  }, []);

  return (
    <div className={cx(cf.dashSurface, css({ p: "5" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Ubicación</p>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "3", lineHeight: "1.5" })}>
        Se muestra en la pestaña "Info" de tu agenda online. Buscá la dirección y, si el pin no cae exactamente
        donde querés, hacé clic en el mapa o arrastralo para ajustarlo.
      </p>

      {!apiKeyConfigured ? (
        <p className={css({ fontSize: "sm", color: "#b45309", bg: "#fffbeb", p: "3", rounded: "md" })}>
          Falta configurar la clave de Google Maps del lado del servidor (VITE_GOOGLE_MAPS_API_KEY).
        </p>
      ) : loadError ? (
        <p className={css({ fontSize: "sm", color: "#b91c1c", bg: "#fef2f2", p: "3", rounded: "md" })}>
          No se pudo cargar Google Maps. Verificá la clave y que Maps JavaScript API / Places API (New) estén
          habilitadas.
        </p>
      ) : (
        <>
          {value.address.trim() ? (
            <p className={css({ fontSize: "sm", color: "#334155", mb: "2" })}>
              Dirección actual: <strong>{value.address}</strong>
            </p>
          ) : null}

          <div ref={autocompleteContainerRef} className={css({ mb: "3", "& gmp-place-autocomplete": { width: "100%" } })} />

          {isLoaded ? (
            <div className={css({ rounded: "lg", overflow: "hidden", borderWidth: "1px", borderColor: "#e2e8f0" })}>
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={center}
                zoom={hasPin ? 16 : 4}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={(e) => {
                  if (e.latLng) placePinAt(e.latLng.lat(), e.latLng.lng());
                }}
              >
                {hasPin ? (
                  <MarkerF
                    position={center}
                    draggable
                    onDragEnd={(e) => {
                      if (e.latLng) placePinAt(e.latLng.lat(), e.latLng.lng());
                    }}
                  />
                ) : null}
              </GoogleMap>
            </div>
          ) : (
            <div
              className={css({
                h: "280px",
                rounded: "lg",
                bg: "#f8fafc",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "sm",
                color: "#94a3b8",
              })}
            >
              Cargando mapa…
            </div>
          )}

          {!hasPin ? (
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "2" })}>
              Todavía no ubicaste el local: buscá una dirección o hacé clic en el mapa.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => onChangeRef.current({ address: "", lat: null, lng: null, placeId: null })}
              className={css({
                fontSize: "xs",
                color: "#64748b",
                mt: "2",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                px: "0",
              })}
            >
              Quitar ubicación
            </button>
          )}
        </>
      )}
    </div>
  );
}
